// leinwandServer.js — Leinwände über den Server (18.10.2026)
//
// Panels (Formen, SV, Brackets, Bruchtest) schicken den Zustand ihrer
// Leinwand an PUT /api/leinwand/:turnierId/:art; die Leinwand-Seiten fragen
// GET …?matte=N&k=<schluessel> ab. Damit läuft der Beamer auf jedem Gerät,
// nicht nur auf dem Rechner des Panels. Der lokale Weg (localStorage +
// BroadcastChannel) bleibt daneben bestehen: auf demselben Rechner ist er
// sofort da, und ohne Turnier in der Adresse ist er der einzige.
import { useEffect, useRef, useState } from 'react';
import { toast } from '../components/common/Toast';

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

// ── Ziel (welches Turnier, welche Matte) ───────────────────────────────
const ziel = { turnierId: null, matte: 0 };
// turnierId → { k, geholt, laeuft }. Seit 21.10.2026 ist das der EINE
// Hallenschlüssel des Turniers (backend/services/hallenschluessel.js). Die
// Leitstelle kann ihn erneuern; darum wird er nach SCHLUESSEL_FRISCH_MS im
// Hintergrund neu geholt (der alte bleibt bis dahin stehen, damit „Leinwand
// öffnen“ nie auf eine Antwort warten muss).
const schluessel = new Map();
const SCHLUESSEL_FRISCH_MS = 30000;

/** Panel meldet, wohin seine Leinwand gehört. Holt den Schlüssel gleich vorab,
 *  damit „Leinwand öffnen“ die Adresse ohne Warten kennt (sonst blockiert
 *  der Browser das Fenster, weil es nicht mehr direkt aus dem Klick kommt). */
export function setzeLeinwandZiel(turnierId, matte = 0) {
  ziel.turnierId = turnierId ? String(turnierId) : null;
  ziel.matte = Number(matte) > 0 ? Number(matte) : 0;
  ladeLeinwandSchluessel(ziel.turnierId);
}

/** Hallenschlüssel des Turniers holen; ist er älter als SCHLUESSEL_FRISCH_MS, neu im Hintergrund. */
export function ladeLeinwandSchluessel(turnierId) {
  const tid = turnierId ? String(turnierId) : null;
  if (!tid) return;
  const da = schluessel.get(tid);
  if (da && (da.laeuft || Date.now() - da.geholt < SCHLUESSEL_FRISCH_MS)) return;
  const eintrag = { k: da?.k || null, geholt: da?.geholt || 0, laeuft: true };
  schluessel.set(tid, eintrag);
  fetch(`/api/leinwand/${tid}/schluessel`, { headers: kopf(), cache: 'no-store' })
    .then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.schluessel) throw new Error(d.error || `Fehler ${r.status}`);
      schluessel.set(tid, { k: d.schluessel, geholt: Date.now(), laeuft: false });
    })
    .catch((e) => {
      if (eintrag.k) { schluessel.set(tid, { ...eintrag, laeuft: false, geholt: Date.now() }); return; }
      schluessel.delete(tid);
      toast.fehler(`Leinwand-Adresse ohne Hallenschlüssel: ${e.message}. Die Leinwand geht dann nur auf diesem Rechner.`);
    });
}

/** Adresse einer Leinwand mit Turnier, Matte und Schlüssel (ohne Turnier: nur der Pfad). */
export function leinwandAdresseFuer(pfad, turnierId, matte = 0) {
  if (!turnierId) return pfad;
  ladeLeinwandSchluessel(turnierId); // nur wenn veraltet, im Hintergrund
  const k = schluessel.get(String(turnierId))?.k;
  const teile = [`t=${turnierId}`];
  if (Number(matte) > 0) teile.push(`m=${Number(matte)}`);
  if (k) teile.push(`k=${k}`);
  return `${pfad}?${teile.join('&')}`;
}

/** Adresse für das Ziel aus setzeLeinwandZiel. */
export function leinwandAdresse(pfad) {
  return leinwandAdresseFuer(pfad, ziel.turnierId, ziel.matte);
}

// ── Senden ────────────────────────────────────────────────────────────
// Höchstens ein Schreibvorgang je Art alle 700 ms, immer der neueste Stand:
// das Formen-Panel schickt jede Sekunde die Uhr, Tastendrücke kommen dichter.
const ausstehend = new Map(); // `${turnier}|${art}|${matte}` → { paket }
let letzterFehler = 0;

async function schreiben(turnierId, art, matte, paket) {
  try {
    const r = await fetch(`/api/leinwand/${turnierId}/${art}`, {
      method: 'PUT', headers: kopf(), body: JSON.stringify({ matte, zustand: paket }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.error || `Fehler ${r.status}`);
    }
  } catch (e) {
    // Nicht bei jedem Takt melden — einmal in 30 Sekunden genügt.
    if (Date.now() - letzterFehler > 30000) {
      letzterFehler = Date.now();
      toast.fehler(`Leinwand nicht erreicht: ${e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message}`);
    }
  }
}

/**
 * Zustand an den Server. Ohne `an` gilt das Ziel aus setzeLeinwandZiel; mit
 * `an = { turnierId, matte }` (Brackets, Bruchtest: das Turnier steht im Kampf).
 */
export function sendeAnServer(art, paket, an = null) {
  const turnierId = an?.turnierId ? String(an.turnierId) : ziel.turnierId;
  const matte = an ? (Number(an.matte) > 0 ? Number(an.matte) : 0) : ziel.matte;
  if (!turnierId) return;
  const schl = `${turnierId}|${art}|${matte}`;
  const offen = ausstehend.get(schl);
  if (offen) { offen.paket = paket; return; }
  ausstehend.set(schl, { paket });
  schreiben(turnierId, art, matte, paket);
  setTimeout(() => {
    const e = ausstehend.get(schl);
    ausstehend.delete(schl);
    if (e && e.paket !== paket) sendeAnServer(art, e.paket, { turnierId, matte });
  }, 700);
}

// ── Empfangen (Leinwand-Seiten) ───────────────────────────────────────
/** t, m, k aus der Adresse der Leinwand. */
export function leinwandParameter() {
  const q = new URLSearchParams(window.location.search);
  return { turnierId: q.get('t'), matte: Number(q.get('m')) > 0 ? Number(q.get('m')) : 0, k: q.get('k') || '' };
}

/**
 * Fragt den Server jede Sekunde nach dem Zustand und ruft `uebernehmen` nur
 * bei neuer Version. Ohne Turnier in der Adresse passiert nichts (dann gilt
 * nur der lokale Weg). Gibt { fehler, aktiv } zurück.
 */
export function useLeinwandVomServer(art, uebernehmen) {
  const [fehler, setFehler] = useState('');
  const version = useRef(-1);
  const rueckruf = useRef(uebernehmen);
  rueckruf.current = uebernehmen;
  const { turnierId, matte, k } = leinwandParameter();

  useEffect(() => {
    if (!turnierId) return undefined;
    let aus = false;
    const holen = async () => {
      try {
        const r = await fetch(`/api/leinwand/${turnierId}/${art}?matte=${matte}&k=${encodeURIComponent(k)}`, {
          cache: 'no-store',
          headers: localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {},
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
        if (aus) return;
        setFehler('');
        if (d.version !== version.current) {
          version.current = d.version;
          if (d.zustand) rueckruf.current(d.zustand);
        }
      } catch (e) {
        if (!aus) setFehler(e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message);
      }
    };
    holen();
    const t = setInterval(holen, 1000);
    return () => { aus = true; clearInterval(t); };
  }, [art, turnierId, matte, k]);

  return { fehler, aktiv: !!turnierId };
}
